"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
export default function RiskAssessmentServices() {
  const {
    ref: headingRef,
    isVisible: headingVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const cityPlannerPrinciples = [{
    number: "01",
    title: "Comprehensive site surveys",
    description: "Our certified arborists conduct detailed health assessments to identify potential hazards before they impact your infrastructure."
  }, {
    number: "02",
    title: "Structural integrity analysis",
    description: "We evaluate root systems and canopy stability to ensure long-term safety for public spaces and municipal assets."
  }, {
    number: "03",
    title: "Regulatory compliance reporting",
    description: "We provide precise documentation that meets all local safety standards and environmental protection requirements."
  }, {
    number: "04",
    title: "Proactive risk mitigation",
    description: "Strategic pruning and maintenance plans designed to reduce liability while preserving the natural beauty of the urban forest."
  }, {
    number: "05",
    title: "Long-term safety monitoring",
    description: "We establish recurring inspection schedules to track tree health and prevent future structural failures."
  }];
  const privateClientPrinciples = [{
    number: "01",
    title: "Personalized property walk-throughs",
    description: "We listen to your concerns about specific trees on your property and provide expert guidance on their health and safety."
  }, {
    number: "02",
    title: "Detailed hazard identification",
    description: "Our team pinpoints deadwood, decay, or structural weaknesses that could threaten your home or family during storms."
  }, {
    number: "03",
    title: "Discreet, professional service",
    description: "We execute all assessments and maintenance with minimal disruption to your daily life and landscape aesthetics."
  }, {
    number: "04",
    title: "Transparent, honest advice",
    description: "We provide clear, jargon-free reports so you can make informed decisions about the trees in your care."
  }, {
    number: "05",
    title: "Ongoing tree health support",
    description: "We act as your dedicated partners in maintaining a safe, vibrant, and healthy landscape for years to come."
  }];
  return <section data-section-id="5069" id="risk-assessment-services" className="bg-background text-foreground py-40 overflow-hidden">
      <div className="mx-auto max-w-[1400px] px-0 lg:px-0">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 lg:gap-0 items-stretch">
          {}
          <div ref={imageRef} className={`relative min-h-[420px] lg:min-h-[720px] transition-all duration-1000 ease-out ${imageVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/arborist_risk_assessment.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/arborist_risk_assessment.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/arborist_risk_assessment.png-wvc-sizes" alt="Certified arborist performing a professional tree risk assessment" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover lg:rounded-r-lg shadow-lg" />
          </div>

          {}
          <div className="px-6 sm:px-10 lg:px-16 xl:px-20 py-16 lg:py-24 flex flex-col justify-center">
            {}
            <div ref={headingRef} className={`transition-all duration-1000 ease-out ${headingVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <p className="text-xs sm:text-sm font-sans uppercase tracking-[0.25em] mb-8 text-accent">
                — Expertise
              </p>

              <h2 className="font-default text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[1.02] font-light text-foreground">
                Professional{" "}
                <span className="italic font-light text-accent">
                  Risk Assessment
                </span>
              </h2>

              <div className="mt-10 h-px w-24 bg-secondary" />

              <p className="mt-10 font-default text-lg sm:text-xl leading-relaxed text-foreground/85" style={{
              maxWidth: "42ch"
            }}>
                Safety is the foundation of a healthy landscape. We provide rigorous, data-driven tree risk assessments to protect your property, your community, and the environment.
              </p>
            </div>

            {}
            <div ref={contentRef} className={`mt-14 transition-all duration-1000 delay-150 ease-out ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <Tabs defaultValue="planners" className="w-full">
                <TabsList className="bg-transparent p-0 h-auto gap-3 flex flex-wrap">
                  <TabsTrigger value="planners" className="font-default italic text-base sm:text-lg px-6 py-2.5 rounded-lg border border-border text-foreground bg-transparent data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary data-[state=active]:shadow-md transition-colors">
                    For Municipalities
                  </TabsTrigger>
                  <TabsTrigger value="private" className="font-default italic text-base sm:text-lg px-6 py-2.5 rounded-lg border border-border text-foreground bg-transparent data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary data-[state=active]:shadow-md transition-colors">
                    For Homeowners
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="planners" className="mt-10 data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:duration-500">
                  <ul className="space-y-8">
                    {cityPlannerPrinciples.map((item, i) => <li data-index={i} key={item.number} className="flex gap-5">
                        <span className="font-sans text-sm font-medium tracking-wider pt-1.5 shrink-0 text-accent">
                          {item.number}
                        </span>
                        <div className="flex-1">
                          <h3 className="font-default italic text-xl sm:text-2xl font-light text-foreground leading-snug">
                            {item.title}
                          </h3>
                          <p className="mt-2 font-default text-base leading-relaxed text-foreground/75 max-w-[48ch]">
                            {item.description}
                          </p>
                        </div>
                      </li>)}
                  </ul>
                </TabsContent>

                <TabsContent value="private" className="mt-10 data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:duration-500">
                  <ul className="space-y-8">
                    {privateClientPrinciples.map((item, i) => <li data-index={i} key={item.number} className="flex gap-5">
                        <span className="font-sans text-sm font-medium tracking-wider pt-1.5 shrink-0 text-accent">
                          {item.number}
                        </span>
                        <div className="flex-1">
                          <h3 className="font-default italic text-xl sm:text-2xl font-light text-foreground leading-snug">
                            {item.title}
                          </h3>
                          <p className="mt-2 font-default text-base leading-relaxed text-foreground/75 max-w-[48ch]">
                            {item.description}
                          </p>
                        </div>
                      </li>)}
                  </ul>
                </TabsContent>
              </Tabs>

              {}
              <p className="mt-14 font-default italic text-base sm:text-lg text-foreground/85">
                <Link to="/GetQuotePage" className="underline underline-offset-[6px] decoration-1 transition-colors hover:opacity-80 text-accent" style={{
                textDecorationColor: "var(--accent)"
              }}>
                  Schedule your professional risk assessment
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}